import React from 'react';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  Row,
} from 'reactstrap';
import ReactDatetime from 'react-datetime';
import moment from 'moment';

interface ExtraPaymentModalProps {
  isOpen: boolean;
  toggle: () => void;
  employees: any[];
  extraPaymentStaff: string;
  setExtraPaymentStaff: (value: string) => void;
  extraPaymentType: string;
  setExtraPaymentType: (value: string) => void;
  extraPaymentAmount: string;
  setExtraPaymentAmount: (value: string) => void;
  extraPaymentAddingDate: string;
  setExtraPaymentAddingDate: (value: string) => void;
  extraPaymentReason: string;
  setExtraPaymentReason: (value: string) => void;
  extraPaymentDocumentSelected: File | null | undefined;
  setExtraPaymentDocumentSelected: (file: File | null) => void;
  extraPaymentReceiptSelected: File | null | undefined;
  setExtraPaymentReceiptSelected: (file: File | null) => void;
  documentInputRef: React.RefObject<HTMLInputElement>;
  receiptInputRef: React.RefObject<HTMLInputElement>;
  handleDocumentChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleReceiptChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleCustomButtonDocumentClick: () => void;
  handleCustomButtonReceiptClick: () => void;
  handleSubmitExtraPayment: (event: React.FormEvent) => Promise<void>;
}

const ExtraPaymentModal = ({
  isOpen,
  toggle,
  employees,
  extraPaymentStaff,
  setExtraPaymentStaff,
  extraPaymentType,
  setExtraPaymentType,
  extraPaymentAmount,
  setExtraPaymentAmount,
  extraPaymentAddingDate,
  setExtraPaymentAddingDate,
  extraPaymentReason,
  setExtraPaymentReason,
  extraPaymentDocumentSelected,
  setExtraPaymentDocumentSelected,
  extraPaymentReceiptSelected,
  setExtraPaymentReceiptSelected,
  documentInputRef,
  receiptInputRef,
  handleDocumentChange,
  handleReceiptChange,
  handleCustomButtonDocumentClick,
  handleCustomButtonReceiptClick,
  handleSubmitExtraPayment,
}: ExtraPaymentModalProps) => {
  return (
    <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={toggle} size={'xl'}>
      <div className="modal-header">
        <h4 className="modal-title">Record a New Extra Payment</h4>
        <div onClick={toggle} className="d-flex p-1 cursor-pointer">
          <i className="fas fa-times" />
        </div>
      </div>
      <div className="modal-body">
        <div className="px-2">
          <Form onSubmit={handleSubmitExtraPayment}>
            <Row>
              <Col lg="6">
                <FormGroup>
                  <label className="form-control-label">Name</label>
                  <Input
                    type="select"
                    value={extraPaymentStaff}
                    required
                    onChange={(e) => setExtraPaymentStaff(e.target.value)}
                  >
                    <option value=""></option>
                    {employees?.map((value: any, index: number) => {
                      return (
                        <option value={value.user.id} key={`codes-${index}`}>
                          {value.user.firstName} {value.user.lastName}
                        </option>
                      );
                    })}
                  </Input>
                </FormGroup>
              </Col>
              <Col lg="6">
                <FormGroup>
                  <label className="form-control-label">Type</label>
                  <Input
                    type="select"
                    value={extraPaymentType}
                    required
                    onChange={(e) => setExtraPaymentType(e.target.value)}
                  >
                    <option value=""></option>
                    <option value="Event Pay">Event Pay</option>
                    <option value="Health Care">Health Care</option>
                    <option value="Reimbursement">Reimbursement</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col lg="6">
                <label className="form-control-label">$ Amount</label>
                <Input
                  type="text"
                  value={extraPaymentAmount}
                  required
                  onChange={(event) => {
                    const numbers = event.target.value.replace(/[^\d]/g, '');
                    const amount = (parseFloat(numbers) / 100).toLocaleString('en-IN', {
                      style: 'currency',
                      currency: 'USD',
                    });
                    setExtraPaymentAmount(String(amount).replaceAll(',', ''));
                  }}
                />
              </Col>

              <Col lg="6">
                <FormGroup>
                  <label className="form-control-label">Date Adding To Payroll</label>
                  <InputGroup className="input-group-alternative">
                    <ReactDatetime
                      inputProps={{
                        placeholder: 'Start Date',
                      }}
                      onChange={(newValue) => {
                        setExtraPaymentAddingDate(`${newValue}`);
                      }}
                      timeFormat={false}
                      value={`${moment(extraPaymentAddingDate).format('MM/DD/yyyy')}`}
                      dateFormat={'MM/DD/yyyy'}
                      closeOnSelect={true}
                    />
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="ni ni-calendar-grid-58" />
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col lg="6">
                <label className="form-control-label">Reason</label>
                <Input
                  type="text"
                  value={extraPaymentReason}
                  required
                  onChange={(event) => setExtraPaymentReason(event.target.value)}
                />
              </Col>
            </Row>

            {extraPaymentType === 'Health Care' && (
              <div className="mt-4">
                <h3>Attach Document Here</h3>
                <div className="d-flex align-items-center">
                  <Button color="primary" onClick={handleCustomButtonDocumentClick} style={{ width: 170 }}>
                    Select Document
                  </Button>
                  <input
                    ref={documentInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleDocumentChange}
                  />
                  {extraPaymentDocumentSelected && (
                    <>
                      <p className="p-0 m-0">Selected Document: {extraPaymentDocumentSelected.name} </p>
                      <div
                        onClick={() => setExtraPaymentDocumentSelected(null)}
                        className="d-flex p-1 cursor-pointer ml-2"
                      >
                        <i className="fas fa-times" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {extraPaymentType === 'Reimbursement' && (
              <div className="d-flex flex-column mt-4" style={{ gap: 20 }}>
                <div>
                  <h3>Attach Document Here</h3>
                  <div className="d-flex align-items-center">
                    <Button color="primary" onClick={handleCustomButtonDocumentClick} style={{ width: 170 }}>
                      Select Document
                    </Button>
                    <input
                      ref={documentInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleDocumentChange}
                    />
                    {extraPaymentDocumentSelected && (
                      <>
                        <p className="p-0 m-0">Selected Document: {extraPaymentDocumentSelected.name} </p>
                        <div
                          onClick={() => setExtraPaymentDocumentSelected(null)}
                          className="d-flex p-1 cursor-pointer ml-2"
                        >
                          <i className="fas fa-times" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <h3>Attach Receipt Here</h3>
                  <div className="d-flex align-items-center">
                    <Button color="primary" onClick={handleCustomButtonReceiptClick} style={{ width: 170 }}>
                      Select Receipt
                    </Button>
                    <input
                      ref={receiptInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleReceiptChange}
                    />
                    {extraPaymentReceiptSelected && (
                      <>
                        <p className="p-0 m-0">Selected Receipt: {extraPaymentReceiptSelected.name} </p>
                        <div
                          onClick={() => setExtraPaymentReceiptSelected(null)}
                          className="d-flex p-1 cursor-pointer ml-2"
                        >
                          <i className="fas fa-times" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-around mt-4">
              <Button className="btn-secondary" onClick={toggle}>
                Cancel
              </Button>
              <Button style={{ background: '#02c412', color: '#FFFFFF', width: '150px' }}>Save</Button>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default ExtraPaymentModal;
